<template>
  <div class="client-offer-detail">
    <section class="page-header">
      <div class="container">
        <router-link :to="{ name: 'ClientOffers' }" class="back-link">
          <i class="fas fa-arrow-left me-1"></i> Retour aux offres
        </router-link>
        <div v-if="offer" class="mt-3">
          <h1 class="page-title">{{ offer.title }}</h1>
          <div class="header-meta">
            <span v-if="offer.formatted_deadline">
              <i class="far fa-calendar me-1"></i>
              {{ offer.formatted_deadline }}
            </span>
            <span v-if="!offer.is_active" class="badge-header badge-closed ms-2">Clôturée</span>
            <span v-else-if="offer.is_expired" class="badge-header badge-expired ms-2">Expirée</span>
            <span v-else class="badge-header badge-active ms-2">Active</span>
          </div>
        </div>
      </div>
    </section>

    <div class="container py-5">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted">Chargement...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-warning">{{ error }}</div>

      <div v-else>
        <!-- Stats row -->
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <div class="stat-box">
              <div class="stat-icon stat-blue"><i class="fas fa-users"></i></div>
              <div>
                <div class="stat-value">{{ stats.total }}</div>
                <div class="stat-label">Candidatures reçues</div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="stat-box">
              <div class="stat-icon stat-orange"><i class="fas fa-user-check"></i></div>
              <div>
                <div class="stat-value">{{ stats.preselected }}</div>
                <div class="stat-label">Présélectionnés</div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="stat-box">
              <div class="stat-icon stat-green"><i class="fas fa-star"></i></div>
              <div>
                <div class="stat-value">{{ stats.selected }}</div>
                <div class="stat-label">Retenus</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Candidates list -->
        <div class="section-card">
          <div class="section-card-header">
            <h3>
              <i class="fas fa-user-check me-2 text-primary"></i>
              Candidats présélectionnés
            </h3>
            <span class="counter-badge">{{ candidates.length }}</span>
          </div>

          <div v-if="candidatesLoading" class="text-center py-4">
            <div class="spinner-border text-primary spinner-border-sm"></div>
            <span class="ms-2 text-muted">Chargement des candidats...</span>
          </div>

          <div v-else-if="!candidates.length" class="empty-state-inner">
            <i class="fas fa-user-slash"></i>
            <h5>Aucun candidat présélectionné</h5>
            <p class="text-muted mb-0">
              Le cabinet n'a pas encore présélectionné de candidats pour cette offre.
            </p>
          </div>

          <div v-else class="candidates-table-wrap">
            <table class="candidates-table">
              <thead>
                <tr>
                  <th>Candidat</th>
                  <th>Contact</th>
                  <th class="text-center">Tests</th>
                  <th>Présélectionné le</th>
                  <th class="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in candidates" :key="c.id">
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar me-2">{{ initials(c.candidate.first_name, c.candidate.last_name) }}</div>
                      <div>
                        <div class="fw-semibold">
                          {{ c.candidate.first_name }} {{ c.candidate.last_name }}
                        </div>
                        <small class="text-muted" v-if="c.candidate.gender">
                          {{ c.candidate.gender === 'M' ? 'M.' : 'Mme' }}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="small text-muted">{{ c.candidate.email }}</div>
                    <div class="small text-muted" v-if="c.candidate.phone">
                      {{ c.candidate.phone }}
                    </div>
                  </td>
                  <td class="text-center">
                    <div class="tests-progress">
                      <div class="progress-bar-wrap">
                        <div
                          class="progress-bar-fill"
                          :style="{ width: testPct(c.tests_progress) + '%' }"
                          :class="{ 'bg-complete': c.tests_progress.all_completed }"
                        ></div>
                      </div>
                      <small class="text-muted">
                        {{ c.tests_progress.completed }}/{{ c.tests_progress.total }}
                      </small>
                    </div>
                  </td>
                  <td class="small text-muted">{{ formatDate(c.preselected_at) }}</td>
                  <td class="text-end">
                    <router-link
                      :to="{ name: 'ClientCandidateResults', params: { applicationId: c.id } }"
                      class="btn btn-primary btn-sm"
                    >
                      <i class="fas fa-chart-bar me-1"></i> Voir les résultats
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Offer description -->
        <div class="section-card mt-4" v-if="offer && offer.description">
          <div class="section-card-header">
            <h3>
              <i class="fas fa-file-alt me-2 text-primary"></i>
              Description du poste
            </h3>
          </div>
          <div class="offer-description" v-html="offer.description"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import clientApi from '@/services/clientApi'

export default {
  name: 'ClientOfferDetail',
  data() {
    return {
      loading: true,
      candidatesLoading: false,
      error: null,
      offer: null,
      stats: { total: 0, preselected: 0, selected: 0 },
      candidates: [],
    }
  },
  mounted() {
    this.load()
  },
  watch: {
    '$route.params.id'() {
      this.load()
    },
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      const id = this.$route.params.id
      try {
        const [offerRes, candidatesRes] = await Promise.all([
          clientApi.get(`/client/job-offers/${id}`),
          clientApi.get(`/client/job-offers/${id}/candidates`),
        ])
        const offerPayload = offerRes.data.data || offerRes.data
        this.offer = offerPayload.offer
        this.stats = offerPayload.stats || this.stats

        const candidatesPayload = candidatesRes.data.data || candidatesRes.data
        this.candidates = candidatesPayload.candidates || []
      } catch (e) {
        this.error = 'Impossible de charger les données de cette offre.'
      } finally {
        this.loading = false
      }
    },
    initials(f, l) {
      const a = f ? f.charAt(0) : ''
      const b = l ? l.charAt(0) : ''
      return (a + b).toUpperCase()
    },
    testPct(p) {
      if (!p || !p.total) return 0
      return Math.round((p.completed / p.total) * 100)
    },
    formatDate(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      } catch (e) {
        return '—'
      }
    },
  },
}
</script>

<style scoped>
.client-offer-detail {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  color: #fff;
  padding: 2.5rem 0 3rem;
}

.back-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.back-link:hover {
  color: #fff;
}

.page-title {
  font-size: 1.9rem;
  font-weight: 700;
  margin-bottom: 0.3rem;
}

.header-meta {
  opacity: 0.9;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.9rem;
}

.badge-header {
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.badge-header.badge-active {
  background: rgba(40, 167, 69, 0.9);
}

.badge-header.badge-closed {
  background: rgba(108, 117, 125, 0.9);
}

.badge-header.badge-expired {
  background: rgba(220, 53, 69, 0.9);
}

.stat-box {
  background: #fff;
  border-radius: 14px;
  padding: 1.3rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 100%;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.stat-blue {
  background: linear-gradient(135deg, #2596be, #29b3cd);
}

.stat-orange {
  background: linear-gradient(135deg, #e95d16, #bc6a3c);
}

.stat-green {
  background: linear-gradient(135deg, #28a745, #20a179);
}

.stat-value {
  font-size: 1.7rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.stat-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.section-card {
  background: #fff;
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f2f5;
}

.section-card-header h3 {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0;
  color: #2c3e50;
}

.counter-badge {
  background: rgba(37, 150, 190, 0.12);
  color: #2596be;
  padding: 4px 12px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.85rem;
}

.empty-state-inner {
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state-inner i {
  font-size: 2.5rem;
  color: #dee2e6;
  margin-bottom: 0.8rem;
}

.candidates-table-wrap {
  overflow-x: auto;
}

.candidates-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 4px;
}

.candidates-table thead th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e3e8ee;
}

.candidates-table tbody tr {
  background: #fff;
  transition: background 0.2s;
}

.candidates-table tbody tr:hover {
  background: #f8f9fa;
}

.candidates-table tbody td {
  padding: 0.8rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid #f0f2f5;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2596be, #29b3cd);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
}

.tests-progress {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.progress-bar-wrap {
  width: 80px;
  height: 6px;
  background: #e9ecef;
  border-radius: 50px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #e95d16, #bc6a3c);
  transition: width 0.3s;
}

.progress-bar-fill.bg-complete {
  background: linear-gradient(90deg, #28a745, #20a179);
}

.btn-primary {
  background: linear-gradient(135deg, #2596be, #29b3cd);
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0.9rem;
  font-weight: 600;
  font-size: 0.8rem;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #1e7a9b, #2596be);
}

.offer-description {
  color: #2c3e50;
  line-height: 1.7;
}

.offer-description :deep(p) {
  margin-bottom: 0.8rem;
}

.offer-description :deep(h1),
.offer-description :deep(h2),
.offer-description :deep(h3),
.offer-description :deep(h4) {
  font-weight: 600;
  color: #2596be;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.offer-description :deep(ul),
.offer-description :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.8rem;
}
</style>
